import React from "react";
import { Col, Divider, Form, Input, InputNumber, Modal, Row, Select, Spin, theme } from "antd";
import ColorButton from "../../common/ColorButton";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import ErrorMessages from "../../common/ErrorMessages";
import useDropdownItem from "../../common/useDropdownItem";
import { URL_TRUCK, URL_CUSTOMER } from "../../constants/urls";
import { DELETE, GET, PATCH, POST } from "../../frameworks/HttpClient";
import _ from 'lodash';

export default function ReceivingLatexModal(props) {
  const { open, onClose, target, onUpdate } = props;
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [vehicleOptions, setVehicleOptions] = React.useState([]);
  const [debouncedSupplier, setDebouncedSupplier] = React.useState('');
  const [debouncedSupplierCode, setDebouncedSupplierCode] = React.useState('');
  const [debouncedVehicle, setDebouncedVehicle] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [form] = Form.useForm();
  const supplier = Form.useWatch('supplier', form);
  const isCreate = target === "add";
  const debounceSupplier = React.useCallback(_.debounce((_searchVal) => { setDebouncedSupplier(_searchVal) }, 800), []);
  const debounceSupplierCode = React.useCallback(_.debounce((_searchVal) => { setDebouncedSupplierCode(_searchVal) }, 800), []);
  const debounceVehicle = React.useCallback(_.debounce((_searchVal) => { setDebouncedVehicle(_searchVal) }, 800), []);

  const customerDropdown = useDropdownItem({ manual: true, url: URL_CUSTOMER.RAW_LATEX_SUPPLIER, label: 'name', value:'id', pagination: true, params: { page_size: 999, name : debouncedSupplier } })
  const customerCodeDropdown = useDropdownItem({ manual: true, url: URL_CUSTOMER.RAW_LATEX_SUPPLIER, label_renderer : (item) => `${item.code}`, value:'id', pagination: true, params: { page_size: 999, code : debouncedSupplierCode } })
  const vehicleDropdown = useDropdownItem({ manual: true, url: URL_TRUCK.VEHICLE, label : 'license_plate', value:'id', pagination: true, params: { page_size: 999, license_plate : debouncedVehicle } })

  const handleOK = async () => {
    setLoading(true);
    setErrorMessages(null);
    try {
      const data = await form.validateFields();
      // create or edit data transaction
      if (isCreate) {
        await POST(URL_TRUCK.TRUCK_TRANSACTION, data);
      } else if (!isCreate) {
        await PATCH(`${URL_TRUCK.TRUCK_TRANSACTION}${target.id}/`, data);
      }

      // update Weight
      if (data.in_weight && data.in_weight !== target.in_weight) {
        const weightdata = {
          truck_transaction: target.id,
          direction: "in",
          weight: data.in_weight,
        };
        await POST(URL_TRUCK.TRUCK_TRANSACTION_WEIGHT, weightdata);
      }

      if (data.out_weight && data.out_weight !== target.out_weight) {
        const weightdata = {
          truck_transaction: target.id,
          direction: "out",
          weight: data.out_weight,
        };
        await POST(URL_TRUCK.TRUCK_TRANSACTION_WEIGHT, weightdata);
      }
      onUpdate();
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setErrorMessages(null);
    try {
      await DELETE(`${URL_TRUCK.TRUCK_TRANSACTION}${id}/`);
      onUpdate();
    } catch (error) {
      setErrorMessages(error.setErrorMessage);
    }
  };

  const {
    token: { colorError, colorWarning, colorSuccess },
  } = theme.useToken();

  const confirmDelete = (id) => {
    Modal.confirm({
      title: "ลบรายการรถส่งยาง",
      icon: <ExclamationCircleOutlined />,
      content: "ยืนยันลบรายการรถส่งยาง",
      okText: "ยืนยัน",
      cancelText: "ปิด",
      maskClosable: true,
      // cancelButtonProps: {style: { backgroundColor : colorError, color : '#FFFFFF'}},
      okButtonProps: { style: { backgroundColor: colorError } },
      onOk: () => handleDelete(id),
    });
  };

  React.useEffect(() => {
    if (open) {
      setErrorMessages(null)
      vehicleDropdown.fetchOptions();
      customerDropdown.fetchOptions({});
      customerCodeDropdown.fetchOptions({});
      if (!isCreate) {
        form.setFieldsValue(target);
        // customerDropdown.addOption({key : target.supplier, id : target.supplier, name: target.supplier_name, label : target.supplier_name});
        // vehicleDropdown.addOption({key : target.vehicle, id : target.vehicle, license_plate: target.vehicle_license_plate});
      }
    } else {
      form.resetFields();
    }
  }, [open]);

  React.useEffect(() => {
    if(open)
      customerDropdown.fetchOptions({});
  },[debouncedSupplier])

  React.useEffect(() => {
    if(open)
      customerCodeDropdown.fetchOptions({});
  },[debouncedSupplierCode])

  React.useEffect(() => {
    if(open)
      vehicleDropdown.fetchOptions();
  },[debouncedVehicle])

  return (
    <Modal
      width={700}
      open={open}
      onCancel={onClose}
      okText={"ตกลง"}
      centered
      cancelText={"ปิด"}
      title={isCreate ? "เพิ่มรถเข้า" : "แก้ไขข้อมูลรถเข้า" }
      destroyOnClose
      footer={[
        !isCreate && (
          <ColorButton
            style={{ float: "left" }}
            type={"primary"}
            onClick={() => confirmDelete(target.id)}
            danger
          >
            ลบ
          </ColorButton>
        ),
        <ColorButton
          onClick={onClose}
        >
          ยกเลิก
        </ColorButton>,
        <ColorButton
          type="primary"
          override={isCreate ? colorSuccess : colorWarning}
          onClick={handleOK}
          loading={loading}
        >
          {isCreate ? "เพิ่ม" : "แก้ไข"}
        </ColorButton>,
      ]}
    >
      <ErrorMessages message={errorMessages} />
      <Form
        form={form}
        layout={'vertical'}
      >
        <Row gutter={10}>
          <Col span={7}>
            <Form.Item name={"code"} label="เลขที่บัตร">
              <Input autoComplete="off" placeholder="เลขที่บัตร"/>
            </Form.Item>
          </Col>

          <Col span={7}>
            <Form.Item
              name={"supplier_code"}
              label="รหัสผู้ขาย"
              rules={[{ required: true, message: 'กรุณากรอกข้อมูล' }]}
            >
            <Select 
              initialvalues={"-"}
              autoComplete="off" 
              placeholder="รหัสผู้ขาย"
              showSearch
              filterOption={false}
              notFoundContent={customerCodeDropdown.loading ? <Spin size="small" /> : "-"}
              onSearch={debounceSupplierCode}
              options={customerCodeDropdown.options}
              allowClear={"-"}
              onClear={() => {
                form.setFieldValue('supplier' ,null)
                vehicleDropdown.fetchOptions();
              }}
              onSelect={(value, option) => {
                form.setFieldValue('supplier', option.id)
                vehicleDropdown.fetchOptions();
              }}
            />
            </Form.Item>
          </Col>

          <Col span={10}>
            <Form.Item
              name={"supplier"}
              label="ผู้ขาย"
              rules={[{ required: true, message: 'กรุณากรอกข้อมูล' }]}
            >
            <Select 
              initialvalues={"-"}
              autoComplete="off" 
              placeholder="ผู้ขาย"
              showSearch
              filterOption={false}
              notFoundContent={customerDropdown.loading ? <Spin size="small" /> : "-"}
              onSearch={debounceSupplier}
              options={customerDropdown.options}
              allowClear={"-"}
              onClear={() => {
                form.setFieldValue('supplier_code' ,null)
                vehicleDropdown.fetchOptions();
              }}
              onSelect={(value, option) => {
                form.setFieldValue('supplier_code', option.id)
                vehicleDropdown.fetchOptions();
              }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={"vehicle"}
              label="ทะเบียน"
              rules={[{ required: true, message: 'กรุณากรอกข้อมูล' }]}
            >
              <Select
                showSearch
                placeholder="ทะเบียน"
                autoComplete="off"
                allowClear
                onClear={() => vehicleDropdown.fetchOptions()}
                options={supplier ? vehicleDropdown.options.filter(item => item.supplier === supplier || item.supplier === null) : vehicleDropdown.options}
                filterOption={false}
                onSearch={debounceVehicle}
                onSelect={async(_, option) => {
                  customerDropdown.fetchOptions();
                  customerCodeDropdown.fetchOptions();
                  if(option.supplier){
                    form.setFieldValue('supplier', option.supplier)
                    form.setFieldValue('supplier_code', option.supplier)
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name={"substitute_license_plate"} label="ทะเบียนแทน">
              <Input autoComplete="off" placeholder="ทะเบียนแทน"/>
            </Form.Item>
          </Col>
        </Row>
          {!isCreate && (
            <>
              <Divider />
              <center
                style={{ fontWeight: "bolder", fontSize: 16, marginBottom: 12 }}
              >
                {"ชั่งน้ำหนักเข้า"}
              </center>
              <Form.Item
                name={"in_weight"}
                style={{
                  alignItems: "center",
                  justifyContent: "center",
                  display: "flex",
                }}
              >
                <InputNumber
                  autoComplete="off"
                  style={{ width: '100%' }}
                  placeholder="น้ำหนัก"
                  addonBefore="น้ำหนัก"
                  addonAfter="กก."
                />
              </Form.Item>

              <Divider />
              <center
                style={{ fontWeight: "bolder", fontSize: 16, marginBottom: 12 }}
              >
                {"ชั่งน้ำหนักออก"}
              </center>
              <Form.Item
                name={"out_weight"}
                style={{
                  alignItems: "center",
                  justifyContent: "center",
                  display: "flex",
                }}
              >
                <InputNumber
                  autoComplete="off"
                  style={{ width: '100%' }}
                  placeholder="น้ำหนัก"
                  addonBefore="น้ำหนัก"
                  addonAfter="กก."
                />
              </Form.Item>
            </>
          )}
      </Form>
    </Modal>
  );
}
